import { useTranslation } from 'contexts/Localization'
import useTheme from 'hooks/useTheme'
import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import { Modal, Flex, Text, CloseIcon, Button, Skeleton, useTooltip } from 'vvs-uikit'
import NftSelectorView from 'components/NftSelectorView'
import { useFarms } from 'state/farms/hooks'
import { useTransactionAdder } from 'state/transactions/hooks'
import { ConfirmStaked } from 'views/Farms/components/NftStakeModal'
import NftBoostFactorCard from 'views/Farms/components/NftBoostFactorCard'
import { useStakeNftsAndBoostFarm, useUnStakeNfts } from 'hooks/boost-farm/useFarmBoostContract'
import BigNumber from 'bignumber.js'
import { useMyMoles } from 'views/VvsMinerMoleMyMoles/hooks/useMyMoles'
import useBoostFactor from 'hooks/boost-farm/useBoostFactor'
import { MAX_NFT_SELECTABLE } from 'config/constants/farmBoost'
import useToast from 'hooks/useToast'
import { getArgoNftAddress, getVvsMinerMoleAddress } from 'utils/addressHelpers'
import useNftBalanceOfUser from 'views/Farms/hooks/useNftBalanceOfUser'
import { NFT_TYPE } from 'config/constants/nftTypes'
import {
  getRewarderV2AddressByPid,
  useStakeNfts as useStakeEcoFarmNfts,
  useUnStakeNfts as useUnStakeEcoFarmNfts,
} from 'hooks/rewarderV2/useRewarderV2Contract'
import { NftDetails } from 'views/VvsMinerMoleMyMoles/types'
import farms from 'config/constants/farms'
import { ecoFarm } from 'config/constants/ecoBoost'
import {
  useFormatLayerForNftDetails,
  useFormatLayerForNftsDetails,
} from 'views/Farms/hooks/useGetEcoFarmNftCollections'
import useNftModalAprDetails, { CALCULATE_TYPE } from 'views/Farms/hooks/useNftModalAprDetails'
import { getArgoMintedUrl, getVVSMinerMoleMintedUrl } from 'utils/mintedNftCollectionUrls'
import { useGetEcoBoostFactor } from 'views/Farms/hooks/useGetEcoBoostFactor'

const StyledText = styled(Text)`
  > a {
    color: ${({ theme }) => theme.colors.primary};
    display: inline-flex;

    &:hover {
      text-decoration: underline;
    }
  }
`

const StyledModal = styled(Modal)`
  border-radius: 18px;
  border: none;

  > div {
    max-height: 80vh;
    max-width: 570px;
    ${({ theme }) => theme.mediaQueries.md} {
      width: 570px;
    }
  }
`

const StyledCloseIcon = styled(CloseIcon)`
  cursor: pointer;
  width: 24px;
  height: 24px;
`
enum Steps {
  viewStake = 'viewStake',
  stakeMore = 'stakeMore',
  unStakeMore = 'unStakeMore',
  confirmStakeMore = 'confirmStakeMore',
  confirmUnStake = 'confirmUnStake',
}

type NftViewStakeModalProps = {
  onDismiss?: () => void
  farmId: number
  boostingNfts: []
  type?: NFT_TYPE
}

type title = {
  modalTitle: React.ReactNode
  onBack: () => void
  hideHeader: boolean
  hideCloseButton: boolean
  hideHeaderPadding: boolean
}

const ViewStake: React.FC<{
  setCurrentStep: (step) => void
  farmId: number
  boostingNftIds: any[]
  type?: NFT_TYPE
  ecoFarmCollections?: NftDetails[]
}> = ({ setCurrentStep, farmId, boostingNftIds, type = NFT_TYPE.MINER_MOLE, ecoFarmCollections = [] }) => {
  const { t } = useTranslation()
  const { theme } = useTheme()
  const currentFarm = farms.find((farm) => farm.pid === farmId)
  const ecoConfig = ecoFarm[currentFarm?.ecoFarm]

  const { data: boostFactorData, isLoading: isBoostFactorDataLoading } = useBoostFactor(
    {
      farmId,
    },
    true,
  )
  const { data: ecoActualBoostFactor, isLoading: isEcoActualBoostFactor } = useGetEcoBoostFactor(farmId)

  const { data: nftBalanceOfUser, isLoading: isLoadingBalanceOfUser } = useNftBalanceOfUser(farmId)

  const renderContent = useMemo(() => {
    const isArgoReachMaxNftSelectNum = boostingNftIds?.length >= (ecoConfig?.maxStakedNum ?? 0)
    const isMinerMoleReachMaxNftSelectNum = boostingNftIds?.length >= MAX_NFT_SELECTABLE

    let info = {
      boostFactor: 1,
      isLoading: false,
      stakedDesc: '',
      currentStakingDesc: '',
      tooltipsContent: <></>,
      isReachMaxNftSelectNum: true,
    }
    switch (type) {
      case NFT_TYPE.ARGO:
        info = {
          ...info,
          boostFactor: ecoActualBoostFactor,
          isLoading: isEcoActualBoostFactor,
          stakedDesc: t('Staked NFTs:'),
          currentStakingDesc: t('Current staking Nfts'),
          isReachMaxNftSelectNum: isArgoReachMaxNftSelectNum,
          tooltipsContent: isArgoReachMaxNftSelectNum ? (
            <div>
              {t('Reach Max limit total maximum %maxNum% NFTs to stake', {
                maxNum: ecoConfig?.maxStakedNum ?? 0,
              })}
            </div>
          ) : (
            <div>
              <StyledText
                dangerouslySetInnerHTML={{
                  __html: t(
                    'Seems you don’t have any available NFTs in wallet. Time to check out the collection at %openUrlTag%Market Place%closeUrlTag%',
                    {
                      openUrlTag: `<a href="${getArgoMintedUrl()}" target="_blank" rel="noreferrer">`,
                      closeUrlTag: '</a>',
                    },
                  ),
                }}
              />
            </div>
          ),
        }
        break
      case NFT_TYPE.MINER_MOLE:
      default:
        info = {
          ...info,
          boostFactor: boostFactorData?.boostFactor,
          isLoading: isBoostFactorDataLoading,
          stakedDesc: t('Staked Miner Moles:'),
          currentStakingDesc: t('Current staking Moles'),
          isReachMaxNftSelectNum: isMinerMoleReachMaxNftSelectNum,
          tooltipsContent:
            boostingNftIds?.length >= MAX_NFT_SELECTABLE ? (
              <div>
                {t('Reach Max limit total maximum %maxNum% Miner Moles to stake', {
                  maxNum: MAX_NFT_SELECTABLE,
                })}
              </div>
            ) : (
              <div>
                <StyledText
                  dangerouslySetInnerHTML={{
                    __html: t(
                      'Seems you don’t have any available Miner Moles in wallet. Time to check out the collection at %openUrlTag%Market Place%closeUrlTag%',
                      {
                        openUrlTag: `<a href="${getVVSMinerMoleMintedUrl()}" target="_blank" rel="noreferrer">`,
                        closeUrlTag: '</a>',
                      },
                    ),
                  }}
                />
              </div>
            ),
        }
        break
    }
    return info
  }, [
    boostFactorData?.boostFactor,
    boostingNftIds?.length,
    ecoActualBoostFactor,
    ecoConfig?.maxStakedNum,
    isBoostFactorDataLoading,
    isEcoActualBoostFactor,
    t,
    type,
  ])

  const {
    targetRef: warningRef,
    tooltip: warningTooltip,
    tooltipVisible: warningTooltipVisible,
  } = useTooltip(renderContent.tooltipsContent, {
    placement: 'top',
  })

  return (
    <>
      <Flex flexDirection="column" background={theme.colors.boostBackground}>
        <Flex padding="24px 24px 18px 24px">
          <Text fontSize="13px" lineHeight="18px" color={theme.colors.text}>
            {renderContent.stakedDesc}
          </Text>
        </Flex>
        <Flex paddingBottom="24px">
          <NftSelectorView
            nftIds={boostingNftIds}
            selectable={false}
            nftBorderColor={theme.colors.boostBackground}
            type={type}
            dataList={ecoFarmCollections}
          />
        </Flex>
      </Flex>

      <Flex flexDirection="column" padding="24px" gap="24px">
        <Flex flexDirection="column" gap="6px">
          <Flex alignItems="center" justifyContent="space-between">
            <Text fontSize="13px" lineHeight="18px" color={theme.colors.textSubtle}>
              {renderContent.currentStakingDesc}
            </Text>
            {boostingNftIds && boostingNftIds?.length ? (
              <Text fontSize="13px" lineHeight="18px" color={theme.colors.text}>
                {boostingNftIds?.length}
              </Text>
            ) : (
              <Skeleton width="46px" />
            )}
          </Flex>
          <Flex alignItems="center" justifyContent="space-between">
            <Text fontSize="13px" lineHeight="18px" color={theme.colors.textSubtle}>
              {t('Current Total boost factor')}
            </Text>
            {renderContent.isLoading ? (
              <Skeleton width="46px" />
            ) : (
              <Text fontSize="13px" lineHeight="18px" color={theme.colors.text}>
                {renderContent.boostFactor}x
              </Text>
            )}
          </Flex>
        </Flex>
        <Flex gap="12px">
          <Flex flex={1}>
            <Button
              variant="primary"
              scale="lg"
              style={{ width: '100%' }}
              onClick={() => {
                setCurrentStep(Steps.stakeMore)
              }}
              ref={warningRef}
              disabled={nftBalanceOfUser === 0 || isLoadingBalanceOfUser || renderContent.isReachMaxNftSelectNum}
            >
              {t('Stake More')}
            </Button>
            {warningTooltipVisible &&
              (nftBalanceOfUser === 0 || renderContent.isReachMaxNftSelectNum) &&
              warningTooltip}
          </Flex>
          <Flex flex={1}>
            <Button
              variant="primary"
              scale="lg"
              style={{ width: '100%' }}
              onClick={() => {
                setCurrentStep(Steps.unStakeMore)
              }}
            >
              {t('Unstake')}
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </>
  )
}

interface StakeMoreProps {
  setCurrentStep: (step) => void
  setSelectedMoleIds: (stakeMoreMoleIds) => void
  stakeMoreMoleIds: string[]
  farmId: number
  boostingNftIds: any[]
  ecoFarmNftCollections?: NftDetails[]
  type?: NFT_TYPE
}

const StakeMore: React.FC<StakeMoreProps> = ({
  setCurrentStep,
  setSelectedMoleIds,
  stakeMoreMoleIds,
  farmId,
  boostingNftIds,
  ecoFarmNftCollections = [],
  type = NFT_TYPE.MINER_MOLE,
}) => {
  const { t } = useTranslation()
  const { molesIds } = useMyMoles()

  const renderContentInfo = useNftModalAprDetails(farmId, type, stakeMoreMoleIds, CALCULATE_TYPE.STAKE_MORE)

  return (
    <>
      <Flex paddingBottom="24px">
        <NftSelectorView
          nftIds={molesIds}
          selectable
          maxSelect={renderContentInfo.maxSelect - boostingNftIds.length}
          selectedNftIds={stakeMoreMoleIds}
          onChange={(selectedNfts) => {
            setSelectedMoleIds(selectedNfts)
          }}
          dataList={ecoFarmNftCollections}
          type={type}
        />
      </Flex>
      <NftBoostFactorCard
        type="plus"
        farmId={farmId}
        selectedNftIds={[...stakeMoreMoleIds, ...boostingNftIds]}
        currentStakingMoles={boostingNftIds?.length}
        currentStakingDesc={renderContentInfo.currentStakingDesc}
        currentSelectedDesc={renderContentInfo.currentSelectedDesc}
        questionHelperText={renderContentInfo.questionHelperText}
        nftBoostFactor={renderContentInfo.nftBoostFactor}
        nftType={type}
      />
      <Flex flex={1} padding="24px">
        <Button
          variant="primary"
          scale="lg"
          style={{ width: '100%' }}
          onClick={() => {
            setCurrentStep(Steps.confirmStakeMore)
          }}
          disabled={stakeMoreMoleIds?.length === 0}
        >
          {t('Continue')}
        </Button>
      </Flex>
    </>
  )
}

interface UnStakeMoreProps {
  unStakeMoleIds: string[]
  farmId: number
  setCurrentStep: (step) => void
  setUnStakeMoleIds: (unStakeMoleIds) => void
  boostingNftIds: any[]
  type?: NFT_TYPE
  ecoFarmCollections?: NftDetails[]
}

const UnStakeMore: React.FC<UnStakeMoreProps> = ({
  setCurrentStep,
  unStakeMoleIds,
  farmId,
  setUnStakeMoleIds,
  boostingNftIds,
  type = NFT_TYPE.MINER_MOLE,
  ecoFarmCollections = [],
}) => {
  const { t } = useTranslation()

  const calculatedNfts = boostingNftIds.filter(
    (boostingNft) => !unStakeMoleIds.some((unStakeNft) => unStakeNft === boostingNft),
  )

  const renderContentInfo = useNftModalAprDetails(farmId, type, unStakeMoleIds, CALCULATE_TYPE.UN_STAKE)

  return (
    <>
      <Flex paddingBottom="24px">
        <NftSelectorView
          nftIds={boostingNftIds}
          selectable
          maxSelect={MAX_NFT_SELECTABLE}
          onChange={(selectedNfts) => {
            setUnStakeMoleIds(selectedNfts)
          }}
          type={type}
          dataList={ecoFarmCollections}
          selectedNftIds={unStakeMoleIds}
        />
      </Flex>
      <NftBoostFactorCard
        type="minus"
        farmId={farmId}
        selectedNftIds={calculatedNfts}
        currentStakingMoles={boostingNftIds.length}
        currentStakingDesc={renderContentInfo.currentStakingDesc}
        currentSelectedDesc={renderContentInfo.currentSelectedDesc}
        questionHelperText={renderContentInfo.questionHelperText}
        nftBoostFactor={renderContentInfo.nftBoostFactor}
        nftType={type}
      />
      <Flex flex={1} padding="24px">
        <Button
          variant="primary"
          scale="lg"
          style={{ width: '100%' }}
          onClick={() => {
            setCurrentStep(Steps.confirmUnStake)
          }}
          disabled={unStakeMoleIds?.length === 0}
        >
          {t('Continue')}
        </Button>
      </Flex>
    </>
  )
}

const NftViewStakeModal: React.FC<NftViewStakeModalProps> = ({
  onDismiss,
  farmId,
  boostingNfts,
  type = NFT_TYPE.MINER_MOLE,
}) => {
  const { t } = useTranslation()
  const { theme } = useTheme()
  const { data: farmsLP } = useFarms()
  const addTransaction = useTransactionAdder()
  const [currentStep, setCurrentStep] = useState<Steps>(Steps.viewStake)
  const [stakeMoreMoleIds, setSelectedMoleIds] = useState<string[]>([])
  const [unStakeMoleIds, setUnStakeMoleIds] = useState<string[]>([])

  const { mutateAsync: boostFarm, isLoading: isBoosting } = useStakeNftsAndBoostFarm()
  const { mutateAsync: unStakeFarm, isLoading: isUnStakeNftLoading } = useUnStakeNfts()

  const { mutateAsync: boostEcoFarm, isLoading: isEcoFarmBoosting } = useStakeEcoFarmNfts(
    getRewarderV2AddressByPid(farmId),
  )
  const { mutateAsync: unStakeEcoFarm, isLoading: isEcoFarmUnStakeLoading } = useUnStakeEcoFarmNfts(
    getRewarderV2AddressByPid(farmId),
  )

  const { toastError } = useToast()

  const boostingNftIds = boostingNfts?.map((nftDetails) => {
    const nftId = nftDetails[1] as BigNumber
    return nftId.toString()
  })

  const { data: userAllNftCollections } = useFormatLayerForNftDetails(farmId)
  const { data: boostingEcoFarmCollections } = useFormatLayerForNftsDetails(getArgoNftAddress(), boostingNftIds, farmId)

  const renderConfirmAction = useMemo(() => {
    let confirmStakeMoreAction
    let confirmUnStakeStakeAction
    let stakeMoreParams = {}
    let unStakeParams = {}
    let isStakeMoreLoading = true
    let isUnStakeMoreLoading = true

    switch (type) {
      case NFT_TYPE.ARGO:
        confirmStakeMoreAction = boostEcoFarm
        confirmUnStakeStakeAction = unStakeEcoFarm
        stakeMoreParams = {
          farmId,
          nfts: stakeMoreMoleIds.map((tokenId) => ({
            tokenContract: getArgoNftAddress(),
            tokenId,
          })),
        }
        unStakeParams = {
          farmId,
          nfts: unStakeMoleIds.map((tokenId) => ({
            tokenContract: getArgoNftAddress(),
            tokenId,
          })),
        }
        isStakeMoreLoading = isEcoFarmBoosting
        isUnStakeMoreLoading = isEcoFarmUnStakeLoading
        break
      case NFT_TYPE.MINER_MOLE:
      default:
        confirmStakeMoreAction = boostFarm
        confirmUnStakeStakeAction = unStakeFarm
        stakeMoreParams = {
          farmId,
          nfts: stakeMoreMoleIds.map((tokenId) => ({
            tokenContract: getVvsMinerMoleAddress(),
            tokenId,
          })),
        }
        unStakeParams = {
          farmId,
          nfts: unStakeMoleIds.map((tokenId) => ({
            tokenContract: getVvsMinerMoleAddress(),
            tokenId,
          })),
        }
        isStakeMoreLoading = isBoosting
        isUnStakeMoreLoading = isUnStakeNftLoading
        break
    }

    return {
      confirmStakeMoreAction,
      stakeMoreParams,
      isStakeMoreLoading,
      confirmUnStakeStakeAction,
      unStakeParams,
      isUnStakeMoreLoading,
    }
  }, [
    boostEcoFarm,
    boostFarm,
    farmId,
    isBoosting,
    isEcoFarmBoosting,
    isEcoFarmUnStakeLoading,
    isUnStakeNftLoading,
    stakeMoreMoleIds,
    type,
    unStakeEcoFarm,
    unStakeFarm,
    unStakeMoleIds,
  ])

  const handleConfirm = useCallback(async () => {
    try {
      if (currentStep === Steps.confirmStakeMore) {
        const { tx } = await renderConfirmAction.confirmStakeMoreAction(renderConfirmAction.stakeMoreParams)
        addTransaction(tx, {
          title: t('Boost Farm & Stake NFT Completed'),
          summary: t('Boost %pair% farm with NFT %nftIds%', {
            pair: farmsLP.find((farm) => farm.pid === farmId)?.lpSymbol,
            nftIds: stakeMoreMoleIds.map((id) => `#${id}`).join(', '),
          }),
        })
      } else {
        const { tx } = await renderConfirmAction.confirmUnStakeStakeAction(renderConfirmAction.unStakeParams)
        addTransaction(tx, {
          title: t('Unstaked NFT'),
          summary: t('Unstake NFT %nftIds% for %pair% farm', {
            pair: farmsLP.find((farm) => farm.pid === farmId)?.lpSymbol,
            nftIds: unStakeMoleIds.map((id) => `#${id}`).join(', '),
          }),
        })
      }
      onDismiss()
    } catch (e) {
      toastError(t('Transaction Failed'), e?.data?.message || e?.message) // smart contract msg || metamask msg
    }
  }, [
    currentStep,
    onDismiss,
    renderConfirmAction,
    addTransaction,
    t,
    farmsLP,
    stakeMoreMoleIds,
    farmId,
    unStakeMoleIds,
    toastError,
  ])

  const { onBack, hideHeader, modalTitle, hideCloseButton, hideHeaderPadding } = useMemo<title>(() => {
    const currentFarm = farms.find((farm) => farm.pid === farmId)
    const ecoConfig = ecoFarm[currentFarm?.ecoFarm]

    let nftTypeInfo = {
      titleBg: '',
      viewStakeTitle: '',
      viewStakeDesc: <></>,
      stakeMoreDesc: '',
      unStakeMoreDesc: '',
    }

    switch (type) {
      case NFT_TYPE.ARGO:
        nftTypeInfo = {
          ...nftTypeInfo,
          titleBg: '#041851;',
          viewStakeTitle: t('View Cronos NFT Staked'),
          stakeMoreDesc: t('Select total maximum %maxNum% NFTs to stake', { maxNum: ecoConfig?.maxStakedNum ?? 0 }),
          unStakeMoreDesc: t('Select NFTs to unstake'),
        }
        break
      case NFT_TYPE.MINER_MOLE:
      default:
        nftTypeInfo = {
          ...nftTypeInfo,
          titleBg: 'linear-gradient(90deg, #002966 0%, #9476BD 75.91%, #BE8E96 100%);',
          viewStakeTitle: t('View NFT Staked on VVS'),
          viewStakeDesc: (
            <Flex flex={1} justifyContent="flex-start">
              <Text fontSize="13px" lineHeight="18px" color={theme.colors.white}>
                {t('NFT boost factor will be determined by the number of VVS Miner Moles staked')}
              </Text>
            </Flex>
          ),
          stakeMoreDesc: t('Select total maximum %maxNum% Miner Moles to stake', { maxNum: MAX_NFT_SELECTABLE }),
          unStakeMoreDesc: t('Select Miner Moles to unstake'),
        }
        break
    }

    let title = {
      modalTitle: undefined,
      onBack: undefined,
      hideHeader: false,
      hideCloseButton: false,
      hideHeaderPadding: false,
    }
    switch (currentStep) {
      case Steps.viewStake:
        title = {
          ...title,
          hideCloseButton: true,
          hideHeaderPadding: true,
          modalTitle: (
            <Flex flexDirection="column" padding="24px" gap="8px" background={nftTypeInfo.titleBg} width="100%">
              <Flex flex={1} justifyContent="space-between">
                <Text fontWeight="600" fontSize="20px" lineHeight="32px" color={theme.colors.white}>
                  {nftTypeInfo.viewStakeTitle}
                </Text>
                <StyledCloseIcon fill={theme.colors.white} onClick={onDismiss} />
              </Flex>
              {nftTypeInfo.viewStakeDesc}
            </Flex>
          ),
        }
        break
      case Steps.stakeMore:
        title = {
          ...title,
          onBack: () => setCurrentStep(Steps.viewStake),
          modalTitle: (
            <Flex gap="12px" flexDirection="column" justifyContent="center" alignItems="center">
              <Text fontWeight="400" fontSize="28px" lineHeight="33px" fontFamily="Effra" color={theme.colors.text}>
                {t('Stake More NFT')}
              </Text>
              <Text fontWeight="400" fontSize="16px" lineHeight="24px" color={theme.colors.textSubtle}>
                {nftTypeInfo.stakeMoreDesc}
              </Text>
            </Flex>
          ),
        }
        break
      case Steps.confirmStakeMore:
        title = {
          ...title,
          modalTitle: t('Confirm Staking More NFT'),
          onBack: () => setCurrentStep(Steps.stakeMore),
        }
        break
      case Steps.unStakeMore:
        title = {
          ...title,
          onBack: () => setCurrentStep(Steps.viewStake),
          modalTitle: (
            <Flex gap="12px" flexDirection="column" justifyContent="center" alignItems="center">
              <Text fontWeight="400" fontSize="28px" lineHeight="33px" fontFamily="Effra" color={theme.colors.text}>
                {t('NFT to Unstake')}
              </Text>
              <Text fontWeight="400" fontSize="16px" lineHeight="24px" color={theme.colors.textSubtle}>
                {nftTypeInfo.unStakeMoreDesc}
              </Text>
            </Flex>
          ),
        }
        break
      case Steps.confirmUnStake:
        title = {
          ...title,
          modalTitle: t('Confirm NFT Unstaking'),
          onBack: () => setCurrentStep(Steps.unStakeMore),
        }
        break

      default:
        break
    }
    return title
  }, [type, currentStep, farmId, t, theme.colors.white, theme.colors.text, theme.colors.textSubtle, onDismiss])

  const renderContent = useMemo<React.ReactNode>(() => {
    let content = <Skeleton height="300px" />

    switch (currentStep) {
      case Steps.viewStake:
        content = (
          <ViewStake
            boostingNftIds={boostingNftIds || []}
            setCurrentStep={setCurrentStep}
            farmId={farmId}
            type={type}
            ecoFarmCollections={boostingEcoFarmCollections}
          />
        )
        break

      case Steps.stakeMore:
        content = (
          <StakeMore
            farmId={farmId}
            setCurrentStep={setCurrentStep}
            setSelectedMoleIds={setSelectedMoleIds}
            stakeMoreMoleIds={stakeMoreMoleIds}
            boostingNftIds={boostingNftIds || []}
            ecoFarmNftCollections={userAllNftCollections}
            type={type}
          />
        )
        break
      case Steps.unStakeMore:
        content = (
          <UnStakeMore
            unStakeMoleIds={unStakeMoleIds}
            setCurrentStep={setCurrentStep}
            setUnStakeMoleIds={setUnStakeMoleIds}
            farmId={farmId}
            boostingNftIds={boostingNftIds || []}
            type={type}
            ecoFarmCollections={boostingEcoFarmCollections}
          />
        )
        break
      case Steps.confirmStakeMore:
        content = (
          <ConfirmStaked
            onClickConfirm={handleConfirm}
            isLoading={isBoosting || isEcoFarmBoosting}
            farmId={farmId}
            selectedNftIds={stakeMoreMoleIds}
            finalBoostingMoleIds={[...boostingNftIds, ...stakeMoreMoleIds]}
            type={type}
            ecoNftCollections={userAllNftCollections.filter((nft) => stakeMoreMoleIds.find((item) => item === nft.id))}
          />
        )
        break
      case Steps.confirmUnStake:
        content = (
          <ConfirmStaked
            onClickConfirm={handleConfirm}
            isLoading={isUnStakeNftLoading || isEcoFarmUnStakeLoading}
            farmId={farmId}
            selectedNftIds={unStakeMoleIds}
            finalBoostingMoleIds={boostingNftIds.filter(
              (boostingNft) => !unStakeMoleIds.some((unStakeNft) => unStakeNft === boostingNft),
            )}
            confirmBtnText={t('Confirm')}
            type={type}
            ecoNftCollections={boostingEcoFarmCollections.filter((boostingNft) =>
              unStakeMoleIds.some((unStakeNft) => unStakeNft === boostingNft.id),
            )}
            calculateType={CALCULATE_TYPE.UN_STAKE}
          />
        )
        break
      default:
        break
    }
    return content
  }, [
    currentStep,
    boostingNftIds,
    farmId,
    type,
    boostingEcoFarmCollections,
    stakeMoreMoleIds,
    userAllNftCollections,
    unStakeMoleIds,
    handleConfirm,
    isBoosting,
    isEcoFarmBoosting,
    isUnStakeNftLoading,
    isEcoFarmUnStakeLoading,
    t,
  ])

  return (
    <StyledModal
      bodyPadding="0px"
      onDismiss={onDismiss}
      onBack={
        currentStep !== Steps.viewStake
          ? () => {
              if (currentStep === Steps.confirmStakeMore) {
                setSelectedMoleIds([])
              }
              if (currentStep === Steps.confirmUnStake) {
                setUnStakeMoleIds([])
              }
              onBack()
            }
          : undefined
      }
      hideHeader={hideHeader}
      title={modalTitle}
      hideCloseButton={hideCloseButton}
      hideHeaderPadding={hideHeaderPadding}
    >
      {renderContent}
    </StyledModal>
  )
}

export default NftViewStakeModal
